import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { withStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import InputLabel from '@material-ui/core/InputLabel';

import formatPrice from 'utils/formatPrice';
import pxToEm from 'styles/pxToEm';
import pxToRem from 'styles/pxToRem';

const styles = theme => ({
  label: {
    display: 'block',
    position: 'relative',
    marginBottom: 0,
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: 700,
    color: theme.palette.grey[600],
    transform: 'none',
  },
  typography: {
    display: 'block',
    padding: '4px 0',
    lineHeight: pxToEm(16, 14),
    minHeight: pxToEm(16, 14),
  },
});

const InventoryStockLineCreateEditUnitProfitFields = ({ variant, HCPCSCodes, business, classes, ...fields }) => {
  const price = variant !== 'Units' ?
    Number(get(fields, 'Price per Box').input.value.toString().replace(/[$,]/g, '')) : null;
  const unitsInBox = variant !== 'Units' ? 
    get(fields, 'Units in Box').input.value : null;
  const retailPrice = Number(get(fields, 'Retail Price').input.value.toString().replace(/[$,]/g, ''));
  const facilityState = get(fields, 'Facility State').input.value;
  const facilityIsRural = get(fields, 'Facility Is Rural').input.value;
  const priceState = facilityState || business['Company Address'].State;
  const priceIsRural = facilityIsRural || business['Company Address']['Is Rural'];
  const mainHCPCCodeName = get(fields, 'HCPC Codes').input.value.length > 0 ? get(fields, 'HCPC Codes').input.value[0][0] : null;
  const mainHCPCCode = HCPCSCodes.find(item => item['HCPC S Code'] === mainHCPCCodeName);
  const medicaidPrice = mainHCPCCode ? mainHCPCCode['HCPC S Code Price'].find(item => item['Price Type'] === 'Medicaid'
      && item.State === priceState) : null;
  const medicarePrice = mainHCPCCode ? mainHCPCCode['HCPC S Code Price'].find(item => item['Price Type'] === 'Medicare'
    && item.State === priceState) : null;
  let retailProfitPerUnit = null;
  let medicareProfitPerUnit = null;
  let medicaidProfitPerUnit = null;
  let medicarePriceLocalized = null;
  let medicaidPriceLocalized = null;
  if (medicarePrice) {
    medicarePriceLocalized = priceIsRural && medicarePrice['Sale Price Rural']
      ? medicarePrice['Sale Price Rural'] : medicarePrice['Sale Price'];
  }
  if (medicaidPrice) {
    medicaidPriceLocalized = priceIsRural && medicaidPrice['Sale Price Rural']
      ? medicaidPrice['Sale Price Rural'] : medicaidPrice['Sale Price'];
  }
  if (variant === 'Kcal Units') {
    const kcalUnitsInItem = get(fields, 'Calories Per Unit').input.value;
    let pricePerUnit = price && unitsInBox && kcalUnitsInItem
      ? ((price / unitsInBox) / kcalUnitsInItem) * 100 : null;
    if (pricePerUnit && Math.round(pricePerUnit) !== pricePerUnit) {
      pricePerUnit = pricePerUnit.toFixed(2);
    }
    retailProfitPerUnit = retailPrice && pricePerUnit ? retailPrice - pricePerUnit : null;

    medicareProfitPerUnit = mainHCPCCode && medicarePriceLocalized && pricePerUnit
      ? medicarePriceLocalized - pricePerUnit : null;
    medicaidProfitPerUnit = mainHCPCCode && medicaidPriceLocalized && pricePerUnit
      ? medicaidPriceLocalized - pricePerUnit : null;
  } else if (variant === 'Boxes') {
    let pricePerUnit = price && unitsInBox ? price / unitsInBox : null;
    if (pricePerUnit && Math.round(pricePerUnit) !== pricePerUnit) {
      pricePerUnit = pricePerUnit.toFixed(2);
    }
    retailProfitPerUnit = retailPrice && pricePerUnit ? retailPrice - pricePerUnit : null;
    medicareProfitPerUnit = mainHCPCCode && medicarePriceLocalized && pricePerUnit
      ? medicarePriceLocalized - pricePerUnit : null;
    medicaidProfitPerUnit = mainHCPCCode && medicaidPriceLocalized && pricePerUnit
      ? medicaidPriceLocalized - pricePerUnit : null;
  } else {
    const pricePerUnit = Number(get(fields, 'Price per Unit').input.value.toString().replace(/[$,]/g, ''));
    retailProfitPerUnit = retailPrice && pricePerUnit ? retailPrice - pricePerUnit : null;

    medicareProfitPerUnit = mainHCPCCode && medicarePriceLocalized && pricePerUnit
      ? medicarePriceLocalized - pricePerUnit : null;
    medicaidProfitPerUnit = mainHCPCCode && medicaidPriceLocalized && pricePerUnit
      ? medicaidPriceLocalized - pricePerUnit : null;
  }
  if (!retailProfitPerUnit && !medicareProfitPerUnit && !medicaidProfitPerUnit) {
    return '';
  }
  return (
    <Fragment>
      {retailProfitPerUnit !== null &&
        <Grid item>
          <InputLabel
            className={classes.label}
            disableAnimation
            shrink={false}
          >
            Retail Unit Profit:
          </InputLabel>
          <Typography className={classes.typography}>
            {retailProfitPerUnit && formatPrice(retailProfitPerUnit)}
          </Typography>
        </Grid>
      }
      {medicareProfitPerUnit !== null &&
        <Grid item>
          <InputLabel
            className={classes.label}
            disableAnimation
            shrink={false}
          >
            Medicare Unit Profit:
          </InputLabel>
          <Typography className={classes.typography}>
            {medicareProfitPerUnit && formatPrice(medicareProfitPerUnit)}
          </Typography>
        </Grid>
      }
      {medicaidProfitPerUnit !== null &&
        <Grid item>
          <InputLabel
            className={classes.label}
            disableAnimation
            shrink={false}
          >
            Medicaid Unit Profit:
          </InputLabel>
          <Typography className={classes.typography}>
            {medicaidProfitPerUnit && formatPrice(medicaidProfitPerUnit)}
          </Typography>
        </Grid>
      }
    </Fragment>
  );
};

InventoryStockLineCreateEditUnitProfitFields.propTypes = {
  variant: PropTypes.oneOf(['Boxes', 'Kcal Units']),
  classes: PropTypes.shape({
    label: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
  }).isRequired,
};

InventoryStockLineCreateEditUnitProfitFields.defaultProps = {
  variant: 'Boxes',
};

export default withStyles(styles)(InventoryStockLineCreateEditUnitProfitFields);



// WEBPACK FOOTER //
// ./src/components/inventory/InventoryStockLineCreateEditDialog/InventoryStockLineCreateEditUnitProfitFields.js